import { handleError } from './errors/errors';
import { getUrlParameters, replaceOrigin } from './url';
// NOTE! Mangling public static functions don't fully resolve correctly in cross reference dependency injections
export class FontLoader {
    static setEnvs(env) {
        FontLoader._creativeConfig = env;
    }
    static setBrowserDocument(doc) {
        FontLoader._browserDocument = doc;
    }
    static async loadFontFace(fontStyle, fontServiceOrigin) {
        const inStudioApp = FontLoader._creativeConfig.environment === 1 /* AdEnvironment.App */;
        try {
            const fontFace = await FontLoader.injectFontFace(fontStyle, fontServiceOrigin, inStudioApp);
            if (!FontLoader._browserDocument.fonts.has(fontFace)) {
                FontLoader._browserDocument.fonts.add(fontFace);
            }
        }
        catch (err) {
            // Do nothing.
            handleError('Could not load font', {
                enabled: inStudioApp,
                contexts: {
                    font: fontStyle,
                    originalError: err
                }
            });
        }
    }
    static async injectFontFace(fontStyle, fontServiceOrigin, trimZWNJ = false) {
        const fontURL = getFontURL(fontStyle, fontServiceOrigin);
        let fontFace = FontLoader._fontStyleMap.get(fontStyle.id);
        const preloadedFontURL = FontLoader._fontStyleURLMap.get(fontStyle.id);
        if (fontFace && preloadedFontURL === fontURL) {
            return await fontFace.load();
        }
        if (fontURL) {
            // Skip U+200C (Zero Width Non Joiner), it should always use fallback
            const unicodeRange = trimZWNJ ? 'U+0-200B, U+200E-10FFFF' : undefined;
            fontFace = new FontFace(`f-${fontStyle.id}`, `url(${fontURL})`, {
                unicodeRange
            });
        }
        else {
            throw new Error('Unknown font style.');
        }
        FontLoader._fontStyleMap.set(fontStyle.id, fontFace);
        FontLoader._fontStyleURLMap.set(fontStyle.id, fontURL);
        return await fontFace.load();
    }
}
FontLoader._fontStyleMap = new Map();
FontLoader._fontStyleURLMap = new Map(); // Load font again if the URL are different (i.e.: diff characters in feed)
FontLoader._browserDocument = window.document;
export function injectFontStyle(document, fontStyle, fontServiceOrigin) {
    const fontURL = getFontURL(fontStyle, fontServiceOrigin);
    if (!fontURL) {
        throw new Error('Failed to get URL of font style');
    }
    const fontFamily = `f-${fontStyle.id}`;
    const fontType = getFontExtension(fontURL);
    const fontStyleSheet = document.createElement('style');
    const fontPreloadLink = document.createElement('link');
    fontPreloadLink.rel = 'preload';
    fontPreloadLink.href = fontURL;
    fontPreloadLink.as = 'font';
    fontPreloadLink.type = `font/${fontType}`;
    fontPreloadLink.crossOrigin = '';
    fontStyleSheet.textContent = `@font-face { font-family: '${fontFamily}'; src: url(${fontURL}) format('${fontType}'); }`;
    document.head.appendChild(fontPreloadLink);
    document.head.appendChild(fontStyleSheet);
}
export function getFontExtension(url) {
    var _a;
    return (_a = getUrlParameters(url).u) === null || _a === void 0 ? void 0 : _a.split('.').pop();
}
export function getFontURL(fontStyle, fontServiceOrigin) {
    let url = fontStyle.src || fontStyle.fontUrl;
    const env = FontLoader._creativeConfig;
    if (env.environment !== 3 /* AdEnvironment.Test */) {
        if (env.environment === 1 /* AdEnvironment.App */ && env.origins.FONTS_AZURE_STORAGE_CDN) {
            url = replaceOrigin(url, env.origins.FONTS_AZURE_STORAGE_CDN);
        }
        if (fontServiceOrigin) {
            // notice, url can be base64 and still work
            url = replaceOrigin(url, fontServiceOrigin);
        }
    }
    return url;
}
export function getFontBlobUrl(url) {
    const fontUrl = new URL(url);
    const encodedFontBlobUrl = fontUrl.searchParams.get('u');
    if (!encodedFontBlobUrl) {
        return url;
    }
    return decodeURIComponent(encodedFontBlobUrl);
}
